import { useMemo, useState } from "react";
import { ErpShell } from "../components/layout/ErpShell.js";
import { useAuth } from "../hooks/useAuth.js";
import { useCanEdit } from "../hooks/useCanEdit.js";
import { useSelectedCompany } from "../hooks/useSelectedCompany.js";
import { useI18n } from "../i18n/I18nProvider.js";
import { useT } from "../i18n/index.js";
import { formatFrenchPlate } from "../lib/plate-format.js";
import { setPin, usePin } from "../hooks/usePin.js";
import { formatMoney } from "../lib/format-fr.js";
import { OperationBoard } from "../components/domain/OperationBoard.js";
import { trpc } from "../lib/trpc.js";

/**
 * F-agenda-erp (CEO 2026-09-18, print do eDSP) — agenda semanal no ERP para
 * o ESCRITÓRIO: RDVs de chantiers E de clientes (carrosseries) numa visão
 * única, estilo eDSP (Semaine N, colunas por dia, Nouveau RDV).
 *
 * Mesma tabela do portal (chantier_appointments): RDV criado aqui com
 * cliente-de-chantier aparece também na agenda do Xavier e do portal do
 * cliente; RDV de cliente sem chantier é exclusivo do escritório.
 */

const inputClass =
  "w-full rounded-md border border-neutral-700 bg-neutral-900 px-3 py-2 text-sm outline-none focus:border-lime";

type ApptRow = {
  id: number;
  chantierId: number | null;
  customerId: number | null;
  date: string;
  time: string;
  plate: string | null;
  vehicleLabel: string | null;
  notes: string | null;
  status: string;
  customerName: string | null;
  chantierName: string | null;
  /** F-agenda-status — status do carro (modelo eDSP) + valor a faturar. */
  carStatus: string | null;
  carAmountHt: number | null;
  carDossierId: number | null;
};

/** Segunda-feira da semana da data. */
function mondayOf(d: Date): Date {
  const out = new Date(d);
  out.setHours(0, 0, 0, 0);
  const dow = (out.getDay() + 6) % 7; // 0 = segunda
  out.setDate(out.getDate() - dow);
  return out;
}
function isoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
/** Nº ISO da semana (como o "Semaine 38" do eDSP). */
function isoWeekNumber(d: Date): number {
  const t = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNum = t.getUTCDay() || 7;
  t.setUTCDate(t.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(t.getUTCFullYear(), 0, 1));
  return Math.ceil(((t.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
}

const INTL_LOCALE: Record<string, string> = {
  fr: "fr-FR",
  pt: "pt-BR",
  en: "en-GB",
  es: "es-ES",
  uk: "uk-UA",
};

export default function Agenda() {
  const t = useT();
  const { locale } = useI18n();
  const { user } = useAuth();
  const canEdit = useCanEdit();
  const companiesQuery = trpc.companies.list.useQuery();
  const accessibleCompanies = (companiesQuery.data ?? []).filter((c) =>
    (user?.allowedCompanyIds ?? []).includes(c.id),
  );
  const [companyId, setCompanyId] = useSelectedCompany(accessibleCompanies);

  const [weekStart, setWeekStart] = useState(() => mondayOf(new Date()));
  const days = useMemo(
    () =>
      Array.from({ length: 7 }, (_, i) => {
        const d = new Date(weekStart);
        d.setDate(d.getDate() + i);
        return d;
      }),
    [weekStart],
  );
  const from = isoDate(days[0]!);
  const to = isoDate(days[6]!);

  const weekQuery = trpc.chantierAgenda.erpWeek.useQuery(
    { companyId: companyId ?? 0, from, to },
    { enabled: companyId != null },
  );
  const optionsQuery = trpc.chantierAgenda.erpOptions.useQuery(
    { companyId: companyId ?? 0 },
    { enabled: companyId != null },
  );
  const utils = trpc.useUtils();
  const invalidate = () => utils.chantierAgenda.erpWeek.invalidate();
  const createMut = trpc.chantierAgenda.erpCreate.useMutation({ onSuccess: invalidate });
  const updateMut = trpc.chantierAgenda.erpUpdate.useMutation({ onSuccess: invalidate });
  const statusMut = trpc.chantierAgenda.erpSetStatus.useMutation({ onSuccess: invalidate });

  // Filtro: todos | chantier específico | clientes sem chantier ("none").
  const [filter, setFilter] = useState<string>("all");
  // F-pin — alfinete global (cliente OU chantier) força o filtro até soltar.
  const pin = usePin();
  const activePin = pin && pin.companyId === companyId ? pin : null;
  const effectiveFilter = activePin?.kind === "chantier" ? String(activePin.id) : filter;
  const rows = (weekQuery.data?.rows ?? []) as ApptRow[];
  const filtered = rows.filter((r) => {
    if (activePin?.kind === "customer" && r.customerId !== activePin.id) return false;
    return effectiveFilter === "all"
      ? true
      : effectiveFilter === "none"
        ? r.chantierId == null
        : r.chantierId === Number(effectiveFilter);
  });
  const byDay = new Map<string, ApptRow[]>();
  for (const r of filtered) {
    const arr = byDay.get(r.date) ?? [];
    arr.push(r);
    byDay.set(r.date, arr);
  }
  // F-agenda-total-filtered (CEO 2026-09-19: o total "vazava" de outros
  // chantiers) — o selo soma SÓ os RDVs visíveis (filtro/alfinete aplicados),
  // um carro contado uma única vez.
  const weekTotalShown = (() => {
    const seen = new Set<number>();
    let sum = 0;
    for (const r of filtered) {
      if (r.carAmountHt == null || r.carDossierId == null || seen.has(r.carDossierId)) continue;
      seen.add(r.carDossierId);
      sum += r.carAmountHt;
    }
    return sum;
  })();

  const [modal, setModal] = useState<
    { mode: "new"; date: string } | { mode: "edit"; appt: ApptRow } | null
  >(null);

  const todayIso = isoDate(new Date());
  const intl = INTL_LOCALE[locale] ?? "fr-FR";
  const dayName = (d: Date) => new Intl.DateTimeFormat(intl, { weekday: "long" }).format(d);
  const dayLabel = (d: Date) =>
    new Intl.DateTimeFormat(intl, { day: "numeric", month: "long" }).format(d);

  const STATUS_LABEL: Record<string, string> = {
    scheduled: t("agendaPage.statusScheduled"),
    arrived: t("agendaPage.statusArrived"),
    done: t("agendaPage.statusDone"),
  };
  const STATUS_NEXT: Record<string, "arrived" | "done" | "scheduled"> = {
    scheduled: "arrived",
    arrived: "done",
    done: "scheduled",
  };
  const STATUS_CLS: Record<string, string> = {
    scheduled: "bg-sky-500/15 text-sky-300 border-sky-500/40",
    arrived: "bg-amber-500/15 text-amber-300 border-amber-500/40",
    done: "bg-lime/15 text-lime border-lime/40",
  };

  if (!user) return null;

  return (
    <ErpShell>
      <main className="mx-auto max-w-[1500px] p-6 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold tracking-wide">📅 {t("agendaPage.title")}</h1>
            <p className="text-sm text-neutral-500">{t("agendaPage.subtitle")}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {activePin && (
              <button
                type="button"
                onClick={() => setPin(null)}
                title={t("erpShell.unpinTitle")}
                className="flex items-center gap-1 rounded-md border border-lime/50 bg-lime/10 px-2.5 py-2 text-sm font-semibold text-lime"
              >
                📌 {activePin.label} ✕
              </button>
            )}
            <select
              value={effectiveFilter}
              disabled={activePin?.kind === "chantier"}
              onChange={(e) => setFilter(e.target.value)}
              className="rounded-md border border-neutral-700 bg-neutral-900 px-3 py-2 text-sm disabled:opacity-60"
            >
              <option value="all">{t("agendaPage.filterAllLabel")}</option>
              {(optionsQuery.data?.chantiers ?? []).map((c) => (
                <option key={c.id} value={String(c.id)}>
                  🏗 {c.name}
                </option>
              ))}
              <option value="none">{t("agendaPage.filterNoChantier")}</option>
            </select>
            {!activePin && filter !== "all" && filter !== "none" && companyId != null && (
              <button
                type="button"
                title={t("erpShell.pinTitle")}
                onClick={() => {
                  const ch = (optionsQuery.data?.chantiers ?? []).find(
                    (c) => c.id === Number(filter),
                  );
                  if (ch) setPin({ kind: "chantier", id: ch.id, label: ch.name, companyId });
                }}
                className="rounded-md border border-neutral-700 px-2.5 py-2 text-sm hover:border-lime"
              >
                📌
              </button>
            )}
            <select
              value={companyId ?? ""}
              onChange={(e) => setCompanyId(Number(e.target.value))}
              className="rounded-md border border-neutral-700 bg-neutral-900 px-3 py-2 text-sm"
            >
              {accessibleCompanies.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-center gap-2">
          <div className="flex items-center overflow-hidden rounded-md border border-neutral-700">
            <button
              type="button"
              onClick={() =>
                setWeekStart((w) => {
                  const d = new Date(w);
                  d.setDate(d.getDate() - 7);
                  return d;
                })
              }
              className="px-3 py-2 text-sm hover:bg-neutral-800"
            >
              −
            </button>
            <span className="border-x border-neutral-700 px-4 py-2 text-sm font-semibold">
              {t("agendaPage.weekPre")} {isoWeekNumber(days[0]!)}
            </span>
            <button
              type="button"
              onClick={() =>
                setWeekStart((w) => {
                  const d = new Date(w);
                  d.setDate(d.getDate() + 7);
                  return d;
                })
              }
              className="px-3 py-2 text-sm hover:bg-neutral-800"
            >
              +
            </button>
          </div>
          <button
            type="button"
            onClick={() => setWeekStart(mondayOf(new Date()))}
            className="rounded-md border border-neutral-700 px-3 py-2 text-sm hover:border-lime"
          >
            {t("agendaPage.todayBtn")}
          </button>
          {weekTotalShown > 0 && (
            <span
              title={t("agendaPage.weekTotalTitle")}
              className="rounded-md bg-lime px-3 py-2 font-mono text-sm font-bold text-on-lime"
            >
              {formatMoney(weekTotalShown, { decimals: 2 })}
            </span>
          )}
          {canEdit && (
            <button
              type="button"
              onClick={() => setModal({ mode: "new", date: todayIso })}
              className="rounded-md bg-lime px-4 py-2 text-sm font-semibold text-on-lime"
            >
              + {t("agendaPage.newRdvBtn")}
            </button>
          )}
        </div>
        <p className="text-center text-[11px] text-neutral-600">{t("agendaPage.statusHint")}</p>

        {/* F-operation-board — o quadro dos carros vive ao lado da agenda:
            quem marca o RDV precisa ver o que está em reparação no pátio. */}
        <OperationBoard companyId={companyId} />

        <div className="grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-7">
          {days.map((d) => {
            const iso = isoDate(d);
            const appts = byDay.get(iso) ?? [];
            const isToday = iso === todayIso;
            return (
              <div
                key={iso}
                className={`rounded-lg border ${isToday ? "border-lime/50" : "border-neutral-800"} bg-neutral-900 flex flex-col`}
              >
                <div
                  className={`rounded-t-lg px-2 py-1.5 text-center text-xs font-bold ${
                    isToday ? "bg-lime text-on-lime" : "bg-sky-600/80 text-white"
                  }`}
                >
                  <span className="capitalize">{dayName(d)}</span> {dayLabel(d)}
                </div>
                <div className="flex-1 space-y-1.5 p-1.5 min-h-20">
                  {appts.length === 0 && (
                    <p className="pt-2 text-center text-[11px] text-neutral-600">
                      {t("agendaPage.emptyDay")}
                    </p>
                  )}
                  {appts.map((a) => (
                    <div
                      key={a.id}
                      className="rounded-md border border-neutral-800 bg-neutral-800 p-1.5 text-xs space-y-1"
                    >
                      <div className="flex items-center justify-between gap-1">
                        <span className="font-mono font-bold text-neutral-100">{a.time}</span>
                        <button
                          type="button"
                          disabled={!canEdit || statusMut.isPending}
                          onClick={() =>
                            statusMut.mutate({
                              companyId: companyId!,
                              appointmentId: a.id,
                              status: STATUS_NEXT[a.status] ?? "arrived",
                            })
                          }
                          className={`rounded border px-1.5 py-0.5 text-[10px] font-semibold ${STATUS_CLS[a.status] ?? STATUS_CLS.scheduled}`}
                        >
                          {STATUS_LABEL[a.status] ?? a.status}
                        </button>
                      </div>
                      <button
                        type="button"
                        onClick={() => canEdit && setModal({ mode: "edit", appt: a })}
                        className="block w-full text-left"
                      >
                        {a.plate && (
                          <p className="font-mono font-semibold tracking-wider text-neutral-100">
                            {formatFrenchPlate(a.plate)}
                          </p>
                        )}
                        {a.vehicleLabel && <p className="text-neutral-300">{a.vehicleLabel}</p>}
                        {a.customerName && (
                          <p className="truncate text-neutral-400">👤 {a.customerName}</p>
                        )}
                        {a.chantierName && (
                          <p className="truncate text-neutral-500">🏗 {a.chantierName}</p>
                        )}
                        {a.notes && <p className="truncate text-neutral-500 italic">{a.notes}</p>}
                        {(a.carStatus || a.carAmountHt != null) && (
                          <span className="mt-1 flex items-center justify-between gap-1">
                            {a.carStatus && (
                              <span
                                className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${CAR_STATUS_CLS[a.carStatus] ?? "bg-neutral-800 text-neutral-300"}`}
                              >
                                {CAR_STATUS_LABEL(t)[a.carStatus] ?? a.carStatus}
                              </span>
                            )}
                            {a.carAmountHt != null && (
                              <span className="font-mono text-[10px] text-neutral-400">
                                HT {formatMoney(a.carAmountHt, { decimals: 2 })}
                              </span>
                            )}
                          </span>
                        )}
                      </button>
                    </div>
                  ))}
                  {canEdit && (
                    <button
                      type="button"
                      onClick={() => setModal({ mode: "new", date: iso })}
                      className="w-full rounded-md border border-dashed border-neutral-800 py-1 text-[11px] text-neutral-600 hover:border-lime hover:text-lime"
                    >
                      +
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {modal && companyId != null && (
          <RdvModal
            companyId={companyId}
            modal={modal}
            customers={optionsQuery.data?.customers ?? []}
            chantiers={optionsQuery.data?.chantiers ?? []}
            saving={createMut.isPending || updateMut.isPending || statusMut.isPending}
            onClose={() => setModal(null)}
            onCreate={async (p) => {
              await createMut.mutateAsync({ companyId, ...p });
              setModal(null);
            }}
            onUpdate={async (id, p) => {
              await updateMut.mutateAsync({ companyId, appointmentId: id, ...p });
              setModal(null);
            }}
            onCancelRdv={async (id) => {
              await statusMut.mutateAsync({ companyId, appointmentId: id, status: "cancelled" });
              setModal(null);
            }}
          />
        )}
      </main>
    </ErpShell>
  );
}

/** F-agenda-status — cores/labels por status do carro (modelo eDSP). */
const CAR_STATUS_CLS: Record<string, string> = {
  orcamento: "bg-sky-500/15 text-sky-300",
  em_reparacao: "bg-amber-500/15 text-amber-300",
  reparado: "bg-orange-500/20 text-orange-300",
  faturado: "bg-violet-500/15 text-violet-300",
  recebido: "bg-lime/15 text-lime",
  pago: "bg-lime text-on-lime",
};
const CAR_STATUS_LABEL = (t: ReturnType<typeof useT>): Record<string, string> => ({
  orcamento: t("agendaPage.statusOrcamento"),
  em_reparacao: t("agendaPage.statusEmReparacao"),
  reparado: t("agendaPage.statusReparado"),
  faturado: t("agendaPage.statusFaturado"),
  recebido: t("agendaPage.statusRecebido"),
  pago: t("agendaPage.statusPago"),
});

type RdvPayload = {
  chantierId: number | null;
  customerId: number | null;
  date: string;
  time: string;
  plate?: string;
  vehicleLabel?: string;
  notes?: string;
};

function RdvModal({
  companyId: _companyId,
  modal,
  customers,
  chantiers,
  saving,
  onClose,
  onCreate,
  onUpdate,
  onCancelRdv,
}: {
  companyId: number;
  modal: { mode: "new"; date: string } | { mode: "edit"; appt: ApptRow };
  customers: Array<{ id: number; displayName: string; kind: string; chantierId: number | null }>;
  chantiers: Array<{ id: number; name: string }>;
  saving: boolean;
  onClose: () => void;
  onCreate: (p: RdvPayload) => Promise<void>;
  onUpdate: (id: number, p: RdvPayload) => Promise<void>;
  onCancelRdv: (id: number) => Promise<void>;
}) {
  const t = useT();
  const edit = modal.mode === "edit" ? modal.appt : null;
  const [date, setDate] = useState(edit?.date ?? (modal.mode === "new" ? modal.date : ""));
  const [time, setTime] = useState(edit?.time ?? "09:00");
  const [customerId, setCustomerId] = useState<number | null>(edit?.customerId ?? null);
  const [chantierId, setChantierId] = useState<number | null>(edit?.chantierId ?? null);
  const [plate, setPlate] = useState(edit?.plate ?? "");
  const [vehicleLabel, setVehicleLabel] = useState(edit?.vehicleLabel ?? "");
  const [notes, setNotes] = useState(edit?.notes ?? "");
  // F-agenda-plate-search — digitou parte da placa → lista de carros da
  // empresa para escolher (preenche placa, veículo, cliente e chantier).
  const [suggestOpen, setSuggestOpen] = useState(false);
  const plateSearch = trpc.chantierAgenda.erpPlateSearch.useQuery(
    { companyId: _companyId, q: plate },
    { enabled: suggestOpen && plate.trim().length >= 2 },
  );

  const payload: RdvPayload = {
    chantierId,
    customerId,
    date,
    time,
    plate: plate.trim() || undefined,
    vehicleLabel: vehicleLabel.trim() || undefined,
    notes: notes.trim() || undefined,
  };
  const valid = /^\d{4}-\d{2}-\d{2}$/.test(date) && /^([01]\d|2[0-3]):[0-5]\d$/.test(time);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md space-y-3 rounded-xl border border-neutral-700 bg-neutral-900 p-4"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-lg font-bold">
          {edit ? t("agendaPage.modalEditTitle") : t("agendaPage.modalNewTitle")}
        </h2>
        <div className="grid grid-cols-2 gap-2">
          <label className="relative block space-y-1 text-xs text-neutral-400">
            {t("agendaPage.plateLabel")}
            <input
              value={plate}
              onChange={(e) => {
                setPlate(e.target.value.toUpperCase());
                setSuggestOpen(true);
              }}
              onFocus={() => setSuggestOpen(true)}
              onBlur={() => setTimeout(() => setSuggestOpen(false), 150)}
              placeholder="AA-123-CD"
              className={`${inputClass} font-mono`}
            />
            {suggestOpen && (plateSearch.data?.rows ?? []).length > 0 && (
              <div className="absolute left-0 right-0 top-full z-10 mt-1 overflow-hidden rounded-md border border-neutral-700 bg-neutral-900 shadow-xl">
                {plateSearch.data!.rows.map((v) => (
                  <button
                    key={v.plate ?? ""}
                    type="button"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      setPlate(v.plate ?? "");
                      setVehicleLabel(
                        [v.vehicleMakeSnapshot, v.vehicleModelSnapshot].filter(Boolean).join(" "),
                      );
                      setCustomerId(v.customerId);
                      if (v.chantierId != null) setChantierId(v.chantierId);
                      setSuggestOpen(false);
                    }}
                    className="block w-full px-2.5 py-1.5 text-left text-xs hover:bg-neutral-800"
                  >
                    <span className="font-mono font-semibold">{v.plate}</span>
                    <span className="text-neutral-400">
                      {" "}
                      · {[v.vehicleMakeSnapshot, v.vehicleModelSnapshot].filter(Boolean).join(" ")}
                    </span>
                    <span className="block truncate text-[10px] text-neutral-500">
                      {v.customerName}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </label>
          <label className="space-y-1 text-xs text-neutral-400">
            {t("agendaPage.vehicleLabel")}
            <input
              value={vehicleLabel}
              onChange={(e) => setVehicleLabel(e.target.value)}
              placeholder="Renault Clio"
              className={inputClass}
            />
          </label>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <label className="space-y-1 text-xs text-neutral-400">
            {t("agendaPage.dateLabel")}
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className={inputClass}
            />
          </label>
          <label className="space-y-1 text-xs text-neutral-400">
            {t("agendaPage.timeLabel")}
            <input
              type="time"
              value={time}
              onChange={(e) => setTime(e.target.value)}
              className={inputClass}
            />
          </label>
        </div>
        <label className="block space-y-1 text-xs text-neutral-400">
          {t("agendaPage.customerLabel")}
          <select
            value={customerId ?? ""}
            onChange={(e) => {
              const id = e.target.value ? Number(e.target.value) : null;
              setCustomerId(id);
              // Herda o chantier do cadastro (pode ser trocado abaixo).
              const c = customers.find((x) => x.id === id);
              if (c?.chantierId != null) setChantierId(c.chantierId);
            }}
            className={inputClass}
          >
            <option value="">{t("agendaPage.noneOption")}</option>
            {customers.map((c) => (
              <option key={c.id} value={c.id}>
                {c.displayName}
              </option>
            ))}
          </select>
        </label>
        <label className="block space-y-1 text-xs text-neutral-400">
          {t("agendaPage.chantierLabel")}
          <select
            value={chantierId ?? ""}
            onChange={(e) => setChantierId(e.target.value ? Number(e.target.value) : null)}
            className={inputClass}
          >
            <option value="">{t("agendaPage.noneOption")}</option>
            {chantiers.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          <span className="block text-[10px] text-neutral-600">
            {t("agendaPage.chantierAutoHint")}
          </span>
        </label>
        <label className="block space-y-1 text-xs text-neutral-400">
          {t("agendaPage.notesLabel")}
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            className={inputClass}
          />
        </label>
        <div className="flex items-center justify-between gap-2 pt-1">
          {edit ? (
            <button
              type="button"
              disabled={saving}
              onClick={() => onCancelRdv(edit.id)}
              className="rounded-md border border-red-500/50 px-3 py-2 text-xs font-semibold text-red-300 hover:bg-red-500/10 disabled:opacity-40"
            >
              ✕ {t("agendaPage.cancelRdvBtn")}
            </button>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-neutral-700 px-3 py-2 text-sm hover:border-neutral-500"
            >
              {t("agendaPage.closeBtn")}
            </button>
            <button
              type="button"
              disabled={saving || !valid}
              onClick={() => (edit ? onUpdate(edit.id, payload) : onCreate(payload))}
              className="rounded-md bg-lime px-4 py-2 text-sm font-semibold text-on-lime disabled:opacity-40"
            >
              {saving ? t("agendaPage.savingBtn") : t("agendaPage.saveBtn")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
